import type { editor } from "monaco-editor";

import { Editor } from "@monaco-editor/react";
import { ReactElement } from "react";

import {
  findJsonPathAtOffset,
  jsonPathToLiquidPath,
} from "../../services/integrations/process-liquid-template";
import { JsonLoadingWrapper } from "./JsonLoadingWrapper";

type MonacoJsonEditorProps = {
  bordered?: boolean;
  className?: string;
  loading: boolean;
  onChange(value: string): void;
  value: string;
};

const MonacoJsonEditor = ({
  bordered = true,
  className,
  loading,
  onChange,
  value,
}: MonacoJsonEditorProps): ReactElement => {
  const handleEditorMount = (editor: editor.IStandaloneCodeEditor): void => {
    editor.addAction({
      contextMenuGroupId: "navigation",
      contextMenuOrder: 1.5,
      id: "copy-jsonpath",
      label: "Copy JSONPath",
      run: async (ed): Promise<void> => {
        const position = ed.getPosition?.();
        const model = ed.getModel?.();

        if (!position || !model) return;

        const value = model.getValue();

        try {
          const offset = model.getOffsetAt(position);

          // find the path at the given offset
          const path = findJsonPathAtOffset(value, offset);
          if (path) {
            const liquidPath = jsonPathToLiquidPath([
              "root",
              ...path.map(String),
            ]);
            await navigator.clipboard.writeText(`{{ ${liquidPath} }}`);
          }
        } catch (e) {
          console.error("Failed to parse JSON or find path", e);
        }
      },
    });
  };

  return (
    <JsonLoadingWrapper loading={loading}>
      <Editor
        className={`${bordered ? "monaco-bordered" : ""} ${className}`}
        height="100%"
        language="json"
        onChange={(value) => onChange(value || "")}
        onMount={handleEditorMount}
        options={{
          automaticLayout: true,
          glyphMargin: false,
          lineNumbers: "off",
          lineNumbersMinChars: 2,
          links: false,
          minimap: { enabled: false },
          padding: { bottom: 4, top: 4 },
          scrollbar: {
            horizontal: "hidden",
            vertical: "auto",
          },
          wordWrap: "off",
        }}
        theme="miru-dark"
        value={value}
        width="100%"
      />
    </JsonLoadingWrapper>
  );
};

export default MonacoJsonEditor;
